import axios from 'axios';
import { v4 as uuid } from 'uuid';
import type { VisitorInteractionType } from '@keyvue/one-api-client';

export default function useVisitorData() {
    const client = axios.create({
        baseURL: import.meta.env.VITE_API_HOST + import.meta.env.VITE_API_BASEPATH,
    });
    const sessionVisitorKey = 'assistent_visitor_id';

    function getSource(isSEA: boolean, isSEO: boolean, referrer: string | null): string {
        if (isSEA) {
            return 'SEA';
        }
        if (isSEO) {
            return 'SEO';
        }

        const isExternalReferrer = referrer && !referrer.includes(window.location.hostname);
        if (isExternalReferrer) {
            return 'referral';
        }

        return 'direct';
    }

    function getUtmData() {
        const urlParams = new URLSearchParams(window.location.search);
        const utmSource = urlParams.get('utm_source');
        const utmMedium = urlParams.get('utm_medium');
        const utmCampaign = urlParams.get('utm_campaign');
        const utmTerm = urlParams.get('utm_term');
        const utmContent = urlParams.get('utm_content');

        return {
            utmSource,
            utmMedium,
            utmCampaign,
            utmTerm,
            utmContent,
        };
    }

    function getDeviceType(): string {
        const userAgent = navigator.userAgent.toLowerCase();

        if (/mobile|iphone|ipod|android.*mobile|windows phone/.test(userAgent)) {
            return 'mobile';
        }

        if (/ipad|tablet|android(?!.*mobile)/.test(userAgent)) {
            return 'tablet';
        }
        return 'desktop';
    }

    function getScreenResolution(): string {
        return `${window.screen.width}x${window.screen.height}`;
    }

    function getUserTimezone(): string {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    function getBrowser(userAgent: string) {
        if (userAgent.includes('Firefox/')) return 'Firefox';
        if (userAgent.includes('Edg/')) return 'Edge';
        if (userAgent.includes('OPR/') || userAgent.includes('Opera')) return 'Opera';
        if (userAgent.includes('Chrome/') && !userAgent.includes('Edg/')) return 'Chrome';
        if (userAgent.includes('Safari/') && !userAgent.includes('Chrome/')) return 'Safari';
        return 'Other';
    }

    function getVisitor() {
        const currentUrl = window.location.href;
        const {
            utmSource,
            utmMedium,
            utmCampaign,
            utmTerm,
            utmContent,
        } = getUtmData();

        const { referrer } = document;

        const { userAgent } = navigator;
        const searchEngines = ['google.', 'bing.', 'yahoo.', 'duckduckgo.', 'startpage.', 'ecosia.', 'safari.'];

        const isSEA = Boolean(utmSource && utmMedium && utmCampaign);
        const isSEO = Boolean(!utmSource && referrer && searchEngines.some((engine) => referrer.includes(engine)));

        return {
            currentUrl,
            utmSource,
            utmMedium,
            utmCampaign,
            utmTerm,
            utmContent,
            referrer,
            userAgent,
            browser: getBrowser(userAgent),
            deviceType: getDeviceType(),
            screenResolution: getScreenResolution(),
            timeZone: getUserTimezone(),
            language: navigator.language,
            source: getSource(isSEA, isSEO, referrer),
        };
    }

    async function gatherVisitorData(assistentId: string) {
        const sessionKey = `visitor_registered_${assistentId}`;

        if (sessionStorage.getItem(sessionKey)) return;

        const visitor = getVisitor();
        const vistiorId = uuid();

        const response = await client.post('/ai/assistent/visitors', {
            id: vistiorId,
            assistentId,
            ...visitor,
        }, {
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (response.status === 204) {
            sessionStorage.setItem(sessionKey, 'true');
            sessionStorage.setItem(sessionVisitorKey, vistiorId);
        }
    }

    async function registerInteraction(interactionType: VisitorInteractionType) {
        const visitorId = sessionStorage.getItem(sessionVisitorKey);

        if (visitorId === null) {
            return;
        }

        const sessionKey = `visitor_${visitorId}_${interactionType.value}`;

        if (sessionStorage.getItem(sessionKey)) {
            return;
        }

        const response = await client.post(`/ai/assistent/visitors/${visitorId}/register_interaction`, {
            interactionId: uuid(),
            interactionType: interactionType.value,
            url: window.location.href,
        }, {
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (response.status === 201) {
            sessionStorage.setItem(sessionKey, 'true');
        }
    }

    return {
        gatherVisitorData,
        registerInteraction,
    };
}
